import React, { useEffect, useState, useMemo } from "react";
import ApiClient from "../services/fuelandmoreapi";
import type { TRequestForFirm } from "@/types";
import "./RequestForFirmList.css";
import { formatUTC } from "../utils/date";

interface RequestForFirmListProps {
  apiUrl: string;
  clientDomain: string;
}

const RequestForFirmList: React.FC<RequestForFirmListProps> = ({
  apiUrl,
  clientDomain,
}) => {
  const [requests, setRequests] = useState<TRequestForFirm[]>([]);
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);

  useEffect(() => {
    const fetchRequests = async () => {
      setLoading(true);
      setError(null);
      try {
        const apiClient = new ApiClient(apiUrl);
        const response = await apiClient.getRequestForFirms(clientDomain, {
          page,
          pageSize,
        });
        const payload = response.data ?? response;
        // Support both paginated shape ({items,total,...}) and legacy array
        const data: TRequestForFirm[] = Array.isArray(payload)
          ? payload
          : (payload.items ?? []);
        setRequests(data);
        if (!Array.isArray(payload) && typeof payload.total === "number") {
          setTotal(payload.total);
        } else {
          setTotal(data.length);
        }
      } catch (err) {
        setError("Failed to fetch requests for firm.");
      } finally {
        setLoading(false);
      }
    };
    if (clientDomain) {
      fetchRequests();
    }
  }, [apiUrl, clientDomain, page, pageSize]);

  // Pagination derived data
  // Because backend now paginates, total pages can't be inferred unless server returns total.
  // Fallback to client-side calculation if server didn't paginate (legacy response).
  const totalPages = useMemo(() => {
    const base = total ?? requests.length;
    return Math.max(1, Math.ceil(base / pageSize));
  }, [total, requests.length]);

  const pagedRequests = useMemo(() => requests, [requests]);

  // Flatten each request's lineItems into individual display rows (HOOK must stay before any early returns)
  const rows = useMemo(() => {
    return pagedRequests.flatMap((req) => {
      const base = {
        key: `${req.created}-${req.vesselImo}-${req.port}`,
        req,
      };
      if (req.lineItems && req.lineItems.length > 0) {
        return req.lineItems.map((li, idx) => ({
          ...base,
          lineIndex: idx,
          fuelGrade: li.fuelGrade,
          indicativePrice: li.indicativePrice,
          quantity: li.quantity,
          unitOfMeasure: li.unitOfMeasure,
          timestamp: (li as any).timestamp ? (li as any).timestamp : undefined,
        }));
      }
      // fallback: use top-level legacy fields if present
      return [
        {
          ...base,
          lineIndex: 0,
          fuelGrade: (req as any).fuelGrade,
          indicativePrice: (req as any).indicativePrice,
          quantity: (req as any).quantity,
          unitOfMeasure: (req as any).unitOfMeasure,
          timestamp: undefined,
        },
      ];
    });
  }, [pagedRequests]);

  const groupCounts = useMemo(() => {
    const map = new Map<TRequestForFirm, number>();
    rows.forEach((r) => map.set(r.req, (map.get(r.req) || 0) + 1));
    return map;
  }, [rows]);

  if (loading) return <div>Loading requests for firm...</div>;
  if (error) return <div className="request-list-error">{error}</div>;

  if (!requests || !Array.isArray(requests) || requests.length === 0) {
    return (
      <div className="request-list-empty">No requests for firm found.</div>
    );
  }

  return (
    <div className="request-for-firm-list-container">
      <h3>Requests For Firm</h3>
      <div className="request-for-firm-pagination-bar">
        <a
          href="#prev"
          className={`pager-link ${page === 1 ? "disabled" : ""}`}
          onClick={(e) => {
            e.preventDefault();
            if (page > 1) setPage(page - 1);
          }}
          aria-disabled={page === 1}
        >
          Prev
        </a>
        <span className="pager-status">
          Page {page} of {totalPages}
        </span>
        <a
          href="#next"
          className={`pager-link ${page === totalPages ? "disabled" : ""}`}
          onClick={(e) => {
            e.preventDefault();
            if (page < totalPages) setPage(page + 1);
          }}
          aria-disabled={page === totalPages}
        >
          Next
        </a>
      </div>
      <table className="request-for-firm-list-table">
        <thead>
          <tr>
            <th>Requestor</th>
            <th>Destination / Port</th>
            <th>Vessel</th>
            <th>Line Item</th>
            <th>Remarks</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => {
            const { req } = row;
            const isFirst = i === 0 || rows[i - 1].req !== req;
            const remainingCount = groupCounts.get(req)!;
            return (
              <tr key={`${row.key}-${row.lineIndex}-${i}`}>
                {isFirst && (
                  <td rowSpan={remainingCount}>
                    <div>
                      {req.email
                        ? `${req.email} (${req.clientDomain})`
                        : req.clientDomain}
                    </div>
                    <div>{formatUTC(req.created, "datetime")}</div>
                  </td>
                )}
                {isFirst && (
                  <td rowSpan={remainingCount}>
                    Destination: {req.destinationPort}
                    <br />
                    ETA:{" "}
                    {req.eta
                      ? new Date(req.eta).toLocaleDateString("en-GB", {
                          timeZone: "UTC",
                        })
                      : ""}
                    <br />
                    Port: {req.port}
                    <br />
                    Laycan:{" "}
                    {req.laycanFrom
                      ? new Date(req.laycanFrom).toLocaleDateString("en-GB", {
                          timeZone: "UTC",
                        })
                      : ""}{" "}
                    -{" "}
                    {req.laycanTo
                      ? new Date(req.laycanTo).toLocaleDateString("en-GB", {
                          timeZone: "UTC",
                        })
                      : ""}
                  </td>
                )}
                {isFirst && (
                  <td rowSpan={remainingCount}>
                    {req.vesselName} ({req.vesselImo})
                  </td>
                )}
                <td>
                  Grade: {row.fuelGrade || "-"}
                  <br />
                  Qty: {row.quantity ?? "-"} {row.unitOfMeasure || ""}
                  <br />
                  Price: {row.indicativePrice ?? "-"}
                  <br />
                  {row.timestamp && (
                    <span className="lineitem-ts">
                      Price TS: {formatUTC(row.timestamp, "datetime")}
                    </span>
                  )}
                </td>
                {isFirst && <td rowSpan={remainingCount}>{req.remarks}</td>}
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="request-for-firm-pagination-bar bottom">
        <a
          href="#prev"
          className={`pager-link ${page === 1 ? "disabled" : ""}`}
          onClick={(e) => {
            e.preventDefault();
            if (page > 1) setPage(page - 1);
          }}
          aria-disabled={page === 1}
        >
          Prev
        </a>
        <span className="pager-status">
          Page {page} of {totalPages}
        </span>
        <a
          href="#next"
          className={`pager-link ${page === totalPages ? "disabled" : ""}`}
          onClick={(e) => {
            e.preventDefault();
            if (page < totalPages) setPage(page + 1);
          }}
          aria-disabled={page === totalPages}
        >
          Next
        </a>
      </div>
    </div>
  );
};

export default RequestForFirmList;
